/**
 * The hulks feature (M9). **CH-14a** (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` H-9): the deep-scan ping
 * finds the current system's seeded hulks in range (`hulks/discover.ts`), records them in the Career
 * (`career.hulks.sites[key].found`, through the career write hook) and lists every found, unfinished hulk as a
 * `hulk` nav point (`reach/navPoints.ts`), so the HUD names it and cruise flies to it. The nearest new find becomes
 * the cruise target (`setNavTarget`) when the pilot holds no target and no route, and is named on comms
 * (`hulks.json text.live.found`).
 *
 * - **On entry** (a session enter hook, which runs before the travel subsystem re-checks a loaded nav target): the
 *   system's seeded hulks (`hulksOf`, with the M7 story overlay, cached per galaxy seed) and the found ones as nav
 *   points.
 * - **Per frame:** reads the Reach event ring from its own cursor for `REV.scanPing` (allocation-free when idle).
 *
 * **CH-12a, the live claim loop** (`hulks/live.ts`, bound to the `hulks` Reach subsystem for its prompts and the
 * squat fight): its sources are the entered system's seeded hulks and the traffic manifest's hulk records (`ow-`
 * keys, `claim` ≥ 1). A completed wreck scan of a `hulk` wreck (`SEV.scanComplete`, read per frame from its own
 * cursor, allocation-free when idle) runs the survey and shows the card in the Reach modal. The kit start and the
 * claim go through the fleet service (CH-7s; the claimed ship ferries to the last docked station); a claim
 * refreshes the finder's nav points.
 *
 * **CH-12e:** the card is CH-12b's modal (its answer acted on by the live loop), and `mapHulks` gives the system map
 * its found, unfinished hulk icons (the maps feature reads it on each system-map rebuild).
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { careerAccess, type CareerAccess } from '../../bridge/m2/career'
import { siteOfStation } from '../../bridge/m2/stationTabs'
import { m2StationId, siteIdOfM2Slug } from '../../bridge/m2/stations'
import type { CareerService } from '../../career/flow'
import type { Career } from '../../career/types'
import type { EventRing } from '../../combat/events'
import { eventSlot, firstReadable } from '../../combat/events'
import { logger } from '../../core/logger'
import type { StationSite, SystemLayout } from '../../galaxy/layout/types'
import { hulksConfig, hulkTableOf } from '../../hulks/data'
import { hulkNavPoints, hulksInPing, markHulksFound, mayTargetFind } from '../../hulks/discover'
import { createHulkLive, type HulkLive, type HulkSource } from '../../hulks/live'
import { hulksOf, type HulkSpot } from '../../hulks/place'
import { bindHulkLive } from '../../hulks/subsystem'
import { REV } from '../../reach/events'
import { clearNavPoints, setNavPoints, type NavPoint } from '../../reach/navPoints'
import type { ReachCore } from '../../reach/runtime/types'
import type { ReachCommand, ReachCommandResult, ReachState } from '../../reach/types'
import { NAV, WRECK_KIND, type M2Runtime } from '../../space/contracts'
import { firstReadableSpace, SEV, spaceEventSlot } from '../../space/events'
import { storySites, type StorySites } from '../../story-link/sites'
import { fillTokens } from '../../story-link/world'
import { WRECK_CLAIM, type LocalManifest } from '../../strategic/types'
import { isHulkRecord } from '../../traffic/encounterPort'
import { HULK_CARD_BUTTON } from '../../ui/hulks/models'
import { postStoryEffects } from '../comms/port'

/** After the Reach (160) and the bridge's system world (162), whose enter hook spawns the hulks first. */
const ORDER = FEATURE_ORDER.reach + 3
const ENTER_HOOK = 'hulks'

/** What the finder needs of the Reach (a narrow view of `ReachRuntime`, so tests fake it). */
export interface HulkFinderReach {
  readonly events: EventRing
  readonly state: Pick<ReachState, 'navTarget' | 'route'>
  command(cmd: ReachCommand): ReachCommandResult
}

export interface HulkFinder {
  /** A system was entered with its seeded hulks (finished ones included; `done` is filtered when listing). */
  enter(systemId: string, spots: readonly HulkSpot[]): void
  /** Reads new Reach events; a ping finds hulks. Allocation-free while no ping is waiting. */
  step(): void
  /** Re-lists the nav points from the Career (after a hulk is finished). */
  refresh(): void
}

/** The finder over a Reach and the Career write hook (exported for the tests; the feature wires it). */
export function createHulkFinder(
  reach: HulkFinderReach,
  career: Pick<CareerAccess, 'career' | 'apply'>,
  /** CH-12a: the ping found new hulks (the nearest first). */
  onFound?: (found: readonly HulkSpot[]) => void,
): HulkFinder {
  let cursor = reach.events.head
  let systemId: string | null = null
  let spots: readonly HulkSpot[] = []

  function refresh(): void {
    if (systemId !== null) setNavPoints(systemId, hulkNavPoints(spots, career.career()))
  }

  function ping(x: number, y: number, z: number, radiusM: number): void {
    if (systemId === null || spots.length === 0) return
    const inRange = hulksInPing(spots, x, y, z, radiusM)
    const found = markHulksFound(career.career(), inRange).found
    const first = found[0] as HulkSpot | undefined
    if (first === undefined) return
    const r = career.apply('hulks found', (c) => markHulksFound(c, inRange).career)
    if (!r.ok) {
      logger.warn(`hulks: the ping's finds were refused (${r.error.code})`)
      return
    }
    refresh()
    onFound?.(found)
    if (mayTargetFind(reach.state)) reach.command({ kind: 'setNavTarget', target: { kind: 'hulk', id: first.key } })
  }

  return {
    enter(id, list) {
      systemId = id
      spots = list
      refresh()
    },
    step() {
      const ring = reach.events
      for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
        const s = eventSlot(i)
        if (ring.type[s] === REV.scanPing) ping(ring.x[s], ring.y[s], ring.z[s], ring.amount[s])
      }
      cursor = ring.head
    },
    refresh,
  }
}

/**
 * CH-12e: the found, unfinished seeded hulks of `layout`'s system for the system map (CH-12b's `systemView` icons):
 * `hulksOf` with the cached story overlay, filtered by `hulkNavPoints`. UI rate (the map's rebuilds).
 */
export function mapHulks(reach: Pick<ReachCore, 'galaxy' | 'layoutOf'>, layout: SystemLayout, career: Career | null): NavPoint[] {
  if (career === null) return []
  const sites = sitesOf(reach.galaxy.seed, () => storySites(reach.galaxy, (id) => reach.layoutOf(id)))
  return sites === null ? [] : hulkNavPoints(hulksOf(reach.galaxy, layout, sites, hulkTableOf(career)), career)
}

/**
 * CH-12c: the Reach station site a Career station id plays (`lastDockedStation` is M2's id: `hub` at home, the site
 * slug `sys_01_st7` elsewhere): the current system first, then the home system, then the system the slug names
 * (`bridge/m2/saves.ts dockedPlace`'s order). Null when no site plays it. The claim's ferry goes to the site id the worker knows.
 */
export function careerDockSite(
  reach: Pick<ReachCore, 'galaxy' | 'layoutOf'> & { readonly session: Pick<ReachCore['session'], 'layout'> },
  stationId: string,
): StationSite | null {
  // The live system's layout first: it holds every station of the system (the strategic sites too), where
  // `layoutOf`'s generated layout may hold fewer.
  const here = siteOfStation(reach.session.layout, stationId)
  if (here !== null) return here
  const home = reach.galaxy.homeSystemId
  const atHome = siteOfStation(reach.layoutOf(home), stationId)
  if (atHome !== null) return atHome
  for (const sys of reach.galaxy.systems) {
    if (sys.id === home || !stationId.startsWith(`${m2StationId(sys.id)}_`)) continue
    const site = siteOfStation(reach.layoutOf(sys.id), stationId)
    if (site !== null) return site
  }
  return null
}

/** The M2 id of the home cluster's Hub (M2's `stations.json`; the home station a claim falls back to, CH-12g). */
export const HOME_HUB_STATION = 'hub'

/**
 * CH-12g: where a claim's ship goes for a Career station id: `careerDockSite`'s site (its id and name), else the site
 * id derived from the M2 slug (`siteIdOfM2Slug`) when its system exists, named `text.live.stationIn` (a station the
 * strategic core added in a system the session has not synced: `layoutOf` lacks it, the worker knows it and validates
 * the id). Null when neither plays it.
 */
export function careerDockTarget(
  reach: Parameters<typeof careerDockSite>[0],
  stationId: string,
  stationIn: string = hulksConfig().text.live.stationIn,
): { readonly id: string; readonly name: string } | null {
  const site = careerDockSite(reach, stationId)
  if (site !== null) return { id: site.id, name: site.name }
  const id = siteIdOfM2Slug(stationId)
  if (id === null) return null
  const sys = reach.galaxy.systems.find((s) => id.startsWith(`${s.id}-st`))
  return sys === undefined ? null : { id, name: fillTokens(stationIn, { system: sys.name }) }
}

/** CH-12a: the manifest's hulk records (an open claim, or a disabled one not yet expired) as live-loop sources. */
export function manifestHulkSources(m: Pick<LocalManifest, 'wrecks' | 'time'>): HulkSource[] {
  const out: HulkSource[] = []
  for (const w of m.wrecks) {
    if (!isHulkRecord(w)) continue
    if (w.claim === WRECK_CLAIM.disabled && w.expiresAt <= m.time) continue
    out.push({ key: w.id, hull: w.hull, seed: w.seed, position: w.pos })
  }
  return out
}

let finder: HulkFinder | null = null
let live: HulkLive | null = null
/** The M2 runtime whose space events the scan reader follows, and its cursor. */
let scanSource: M2Runtime | null = null
let scanCursor = 0
let offManifest: (() => void) | null = null
/** Sessions that already have the enter hook (a hook name registers once per session). */
const hooked = new WeakSet<object>()
/** The M7 story overlay by galaxy seed (hulk placement keeps clear of its sites); null when it failed. */
let sitesCache: { readonly seed: number; readonly sites: StorySites | null } | null = null

function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

function sitesOf(seed: number, build: () => StorySites): StorySites | null {
  if (sitesCache === null || sitesCache.seed !== seed) {
    let sites: StorySites | null = null
    try {
      sites = build()
    } catch (err: unknown) {
      logger.warn('hulks: the story sites overlay failed; no seeded hulks to find', err)
    }
    sitesCache = { seed, sites }
  }
  return sitesCache.sites
}

let said = 0
/** A one-line message on comms (M7's `say`). */
function say(text: string): void {
  postStoryEffects([{ type: 'callback', callbackId: `hulks.say:${++said}`, channel: 'comms', text }])
}

const feature: GameFeature = {
  name: 'hulks',
  order: ORDER,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    const m2 = ctx.services.maybe('m2')
    if (reach === null || m2 === undefined || !hasWriteHook(m2)) return
    const access = careerAccess(m2)
    const t = hulksConfig().text
    const f = createHulkFinder(reach, access, (found) => {
      const first = found[0]
      if (first !== undefined) say(fillTokens(t.live.found, { hull: t.hulls[first.hull] ?? first.hull }))
    })
    finder = f
    let cardOpen = false
    const l = createHulkLive({
      career: () => access.career(),
      apply: (name, cmd) => access.apply(name, cmd),
      // The fleet service comes up after this feature (fleetLink, order 172): read it at use.
      fleet: () => ctx.services.maybe('fleet') ?? null,
      // CH-12e: CH-12b's card modal; the live loop acts on the answer (start the kit, abandon the claim).
      showCard: async (card) => {
        if (cardOpen) return HULK_CARD_BUTTON.close
        cardOpen = true
        try {
          const { showModal } = await import('../../reach/ui/modal')
          return await showModal(ctx.overlayRoot, card, ctx.ui)
        } finally {
          cardOpen = false
        }
      },
      say,
      claimed: () => finder?.refresh(),
      dockStation: (id) => careerDockTarget(reach, id, t.live.stationIn),
      homeStation: () => careerDockTarget(reach, HOME_HUB_STATION, t.live.stationIn),
    })
    live = l
    bindHulkLive(l)
    scanSource = m2
    scanCursor = m2.space.events.head
    if (hooked.has(reach.session)) return
    hooked.add(reach.session)
    reach.session.addEnterHook(ENTER_HOOK, (layout: SystemLayout) => {
      // The live M2 runtime (re-read each entry, so a re-init's career is used); null once disposed: nobody reads the spots.
      const source = scanSource
      if (source === null) return
      const sites = sitesOf(reach.galaxy.seed, () => storySites(reach.galaxy, (id) => reach.layoutOf(id)))
      // M10 P3b2: the career's placement table (absent = 1).
      const spots = sites === null ? [] : hulksOf(reach.galaxy, layout, sites, hulkTableOf(source.career()))
      finder?.enter(layout.systemId, spots)
      live?.setSeeded(layout.systemId, layout.region, spots, sites !== null)
    })
  },
  frame(ctx: GameContext): void {
    finder?.step()
    const l = live
    const m2 = scanSource
    if (l === null || m2 === null) return
    // The strategic link comes up after this feature (170): subscribe to its manifests once it is there.
    if (offManifest === null) {
      const client = ctx.services.maybe('strategic')
      if (client !== undefined) offManifest = client.onManifest((m) => l.setOffscreen(m.systemId, manifestHulkSources(m)))
    }
    const ring = m2.space.events
    for (let i = firstReadableSpace(ring, scanCursor); i < ring.head; i++) {
      const s = spaceEventSlot(i)
      if (ring.type[s] !== SEV.scanComplete || ring.a[s] !== NAV.wreck) continue
      const wrecks = m2.space.salvage.wrecks
      const w = ring.b[s]
      if (w < 0 || w >= wrecks.capacity || wrecks.alive[w] === 0 || wrecks.kind[w] !== WRECK_KIND.hulk) continue
      l.scanned(wrecks.key[w], ctx.reach?.career.scannerMk() ?? 1)
    }
    scanCursor = ring.head
  },
  dispose(): void {
    finder = null
    live = null
    scanSource = null
    offManifest?.()
    offManifest = null
    bindHulkLive(null)
    clearNavPoints()
  },
}

export default feature
